import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ResetRequestStep from './steps/ResetRequestStep';
import VerifyStep from '../onboarding/steps/VerifyStep';
import NewPasswordStep from './steps/NewPasswordStep';
import ResetSuccessStep from './steps/ResetSuccessStep';
import { apiHandler } from '../../../../api/apihandler';
import { FORGOT_PASSWORD, FORGOT_PASSWORD_SAVE } from '../../../../api/constants';
import { apiErrorMessage, apiSucceeded } from '../onboarding/apiUtils';
import '../onboarding/onboarding.css';

const RESEND_SECONDS = 60;

// NOTE: no reference implementation was available for this screen (only the
// email signup screen was provided), so the request/resend calls both use
// FORGOT_PASSWORD, and the OTP is verified together with the new password in
// a single FORGOT_PASSWORD_SAVE call — a reasonable but unverified guess at
// the real contract. Confirm against the backend before relying on it.

const ForgotPasswordFlow = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [method, setMethod] = useState(searchParams.get('method') === 'phone' ? 'phone' : 'email');
  const [step, setStep] = useState(1);
  const [completed, setCompleted] = useState(false);

  const [contact, setContact] = useState({ email: '', phone: '', countryCode: '+1' });
  const [otp, setOtp] = useState(Array(6).fill(''));
  const [resendSeconds, setResendSeconds] = useState(RESEND_SECONDS);
  const [contactError, setContactError] = useState('');
  const [verifyError, setVerifyError] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [passwords, setPasswords] = useState({ password: '', confirmPassword: '' });

  useEffect(() => {
    if (step !== 2 || resendSeconds <= 0) return undefined;
    const timer = setInterval(() => {
      setResendSeconds((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [step, resendSeconds]);

  const identifier = method === 'email' ? contact.email.trim().toLowerCase() : contact.phone.replace(/\D/g, '');

  const handleToggleMethod = () => {
    setMethod((m) => (m === 'email' ? 'phone' : 'email'));
    setContactError('');
  };

  const requestCode = async () => {
    const field = method === 'email' ? { email: identifier } : { mobile: identifier, country_code: contact.countryCode };
    return apiHandler(FORGOT_PASSWORD, 'POST', field);
  };

  const handleSendCode = async () => {
    setSendingCode(true);
    setContactError('');
    const res = await requestCode();
    setSendingCode(false);

    if (!apiSucceeded(res)) {
      setContactError(apiErrorMessage(res, 'Unable to send reset code.'));
      return;
    }

    setOtp(Array(6).fill(''));
    setResendSeconds(RESEND_SECONDS);
    setVerifyError('');
    setStep(2);
  };

  const handleResend = async () => {
    const res = await requestCode();
    if (apiSucceeded(res)) {
      setOtp(Array(6).fill(''));
      setResendSeconds(RESEND_SECONDS);
      setVerifyError('');
    } else {
      setVerifyError(apiErrorMessage(res, 'Unable to resend code.'));
    }
  };

  const handleVerify = () => {
    // No dedicated verify-only endpoint is confirmed — the OTP is checked
    // together with the new password on the final step.
    setVerifyError('');
    setStep(3);
  };

  const handleResetPassword = async () => {
    setSaving(true);
    setSaveError('');
    const field = method === 'email' ? { email: identifier } : { mobile: identifier, country_code: contact.countryCode };
    const res = await apiHandler(FORGOT_PASSWORD_SAVE, 'POST', {
      ...field,
      otp: otp.join(''),
      password: passwords.password,
    });
    setSaving(false);

    if (apiSucceeded(res)) {
      setCompleted(true);
    } else {
      setSaveError(apiErrorMessage(res, 'Unable to reset your password.'));
    }
  };

  const goBack = () => {
    if (step === 1) {
      navigate('/auth/sign-in');
      return;
    }
    setStep((s) => s - 1);
  };

  if (completed) {
    return <ResetSuccessStep />;
  }

  return (
    <div className="ob-root">
      {step === 1 && (
        <ResetRequestStep
          method={method}
          contact={contact}
          setContact={setContact}
          onToggleMethod={handleToggleMethod}
          onSubmit={handleSendCode}
          onBack={goBack}
          submitting={sendingCode}
          error={contactError}
        />
      )}
      {step === 2 && (
        <VerifyStep
          step={2}
          totalSteps={3}
          method={method}
          contact={contact}
          otp={otp}
          setOtp={setOtp}
          resendSeconds={resendSeconds}
          onResend={handleResend}
          onSubmit={handleVerify}
          onBack={goBack}
          error={verifyError}
        />
      )}
      {step === 3 && (
        <NewPasswordStep
          passwords={passwords}
          setPasswords={setPasswords}
          onSubmit={handleResetPassword}
          onBack={goBack}
          submitting={saving}
          error={saveError}
        />
      )}
    </div>
  );
};

export default ForgotPasswordFlow;
